(ns frontend.view.settings2.security.view
  (:require [clojure.string :as string]
            [common.features.plan :as plan]
            [common.localization :as loc]
            [common.schema.registry :as registry]
            [common.security.authentication :as authentication]
            [common.security.pem :as pem]
            [common.connection-lifetime :as connection-lifetime]
            [common.session-timeout :as session-timeout]
            [common.url :as url]
            [common.util :as cu]
            [frontend.db :as app-db]
            [frontend.logging :as logging]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.page-components.zap-toolbar :as zap-toolbar]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.utils :as fu]
            [frontend.view.not-found :as not-found]
            [frontend.view.settings2.security.db :as db]
            [frontend.view.settings2.security.events :as events]
            [frontend.zap-time :as zap-time]
            [reagent.core :as r]
            [ui.components.breadcrumbs.view :as breadcrumbs]
            [ui.components.radix-ui.tooltip.view :as tooltip]
            [ui.components.text.view :as text]
            [frontend.view.settings2.sso.view :as new-sso-2025]
            [ui.dom-globals :as dg]
            [ui.editors.labelled-input.view :as labelled-input]))

(defonce state (r/atom {}))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_e]
                 (routing/set-view! [:account-settings :tab-key "security"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :security)]])

(defn- session-timeout-options
  "Build dropdown options with localized labels"
  []
  (->> session-timeout/timeout-options
       (map (fn [{:keys [value key]}]
              {:value value
               :label (loc/tr key)}))))

(defn- session-timeout-dropdown
  "Dropdown for selecting session timeout value"
  [{:keys [tenant-cursor attribute label tooltip-text]}]
  (let [old-value (get @tenant-cursor attribute)
        options (session-timeout-options)
        current-option (->> options
                            (filter #(= (:value %) old-value))
                            first)]
    [:div {:class "flex flex-col gap-px"}
     [:div {:class "flex items-center gap-1"}
      [:label {:class "font-semibold text-sm leading-none m-0"} label]
      [tooltip/view
       {}
       [:i.fal.fa-info-circle.text-base-light-text-paler.cursor-help.text-sm.m-0]
       [:div {:class "max-w-sm text-sm whitespace-pre-line"} tooltip-text]]]
     [labelled-input/view
      {:data-type :data.type/keyword
       :value     current-option
       :options   options
       :on-change (fn [new-option]
                    (let [new-value (if (map? new-option)
                                      (:value new-option)
                                      new-option)]
                      (when-not (= new-value old-value)
                        (swap! tenant-cursor assoc attribute new-value)
                        (events/save-tenant!
                         (:db/id @tenant-cursor)
                         attribute
                         new-value
                         (fn [_] (logging/info "Session timeout updated"))
                         (fn [_]
                           (swap! tenant-cursor assoc attribute old-value)
                           (logging/error "Failed to update session timeout"))))))}]]))

(defn- connection-lifetime-options
  "Build dropdown options for connection lifetime with localized labels"
  []
  (->> connection-lifetime/lifetime-options
       (map (fn [{:keys [value key]}]
              {:value value
               :label (loc/tr key)}))))

(defn- connection-lifetime-dropdown
  "Dropdown for selecting max connection lifetime"
  [{:keys [tenant-cursor attribute label tooltip-text]}]
  (let [old-value (get @tenant-cursor attribute)
        options (connection-lifetime-options)
        current-option (->> options
                            (filter #(= (:value %) old-value))
                            first)]
    [:div {:class "flex flex-col gap-px"}
     [:div {:class "flex items-center gap-1"}
      [:label {:class "font-semibold text-sm leading-none m-0"} label]
      [tooltip/view
       {}
       [:i.fal.fa-info-circle.text-base-light-text-paler.cursor-help.text-sm.m-0]
       [:div {:class "max-w-sm text-sm whitespace-pre-line"} tooltip-text]]]
     [labelled-input/view
      {:data-type :data.type/keyword
       :value     current-option
       :options   options
       :on-change (fn [new-option]
                    (let [new-value (if (map? new-option)
                                      (:value new-option)
                                      new-option)]
                      (when-not (= new-value old-value)
                        (swap! tenant-cursor assoc attribute new-value)
                        (events/save-tenant!
                         (:db/id @tenant-cursor)
                         attribute
                         new-value
                         (fn [_] (logging/info "Connection lifetime updated"))
                         (fn [_]
                           (swap! tenant-cursor assoc attribute old-value)
                           (logging/error "Failed to update connection lifetime"))))))}]]))

(defn- legacy-session-timeout-view
  "Legacy session timeout view using numeric hours input.
   Used when migration to keyword-based timeouts hasn't been run yet."
  [tenant-cursor]
  (fn []
    (let [attribute :tenant/session-timeout
          old-value (get @tenant-cursor attribute)
          value-in-hours (when old-value (/ old-value 60))]
      [:div {:class "flex flex-col gap-6"}
       ;; Section header
       [:div {:class "flex flex-col gap-2"}
        [:div {:class "font-bold text-xl"} (loc/tr :security/session-timeout-title)]
        [:p {:class "text-base-light-text-paler text-sm whitespace-pre-line m-0"}
         (loc/tr :company-settings-session-security-part-1)]]

       [:div {:class "flex flex-col gap-2"}
        [:label {:class "font-semibold text-sm"} (loc/tr :company-settings-session-set-time)]
        [:div {:class "flex items-center gap-2"}
         [labelled-input/view
          {:label        (loc/tr :company-settings-session-hours)
           :min-decimals 0
           :max-decimals 2
           :data-type    :data.type/number
           :value        value-in-hours
           :on-change    (fn [new-value]
                           (swap! tenant-cursor assoc attribute new-value))
           :on-blur      (fn [new-value]
                           (let [mins-val (* 60 new-value)]
                             (when-not (= mins-val old-value)
                               (swap! tenant-cursor assoc attribute mins-val)
                               (events/save-tenant!
                                (:db/id @tenant-cursor)
                                attribute
                                mins-val
                                (fn [_] (logging/info "Session timeout updated"))
                                (fn [_]
                                  (when (= mins-val (get @tenant-cursor attribute))
                                    (swap! tenant-cursor assoc attribute old-value)))))))}]
         [:span {:class "text-xs text-base-light-text-paler"}
          (loc/tr :company-settings-session-security-part-2)]]]])))

(defn- new-session-timeout-settings
  "New session timeout settings section with dropdown selectors.
   Used when migration to keyword-based timeouts has been run."
  [tenant-cursor]
  (let [plugin-feature? (plan/available :feature/outlook-addin @tenant-cursor :role/admin)]
    [:div {:class "flex flex-col gap-6"}
     ;; Section header
     [:div {:class "flex flex-col gap-2"}
      [:div {:class "font-bold text-xl"} (loc/tr :security/session-timeout-title)]
      [:p {:class "text-base-light-text-paler text-sm whitespace-pre-line m-0"}
       (loc/tr :security/session-timeout-description)]]

     ;; Web application timeout
     [session-timeout-dropdown
      {:tenant-cursor tenant-cursor
       :attribute     :tenant/web-session-timeout-kw
       :label         (loc/tr :security/web-session-timeout-label)
       :tooltip-text  (loc/tr :security/web-session-timeout-tooltip)}]

     ;; Add-in timeout (behind feature flag)
     (when plugin-feature?
       [session-timeout-dropdown
        {:tenant-cursor tenant-cursor
         :attribute     :tenant/addin-session-timeout-kw
         :label         (loc/tr :security/addin-session-timeout-label)
         :tooltip-text  (loc/tr :security/addin-session-timeout-tooltip)}])

     ;; Max connection lifetime (behind feature flag)
     (when plugin-feature?
       [connection-lifetime-dropdown
        {:tenant-cursor tenant-cursor
         :attribute     :tenant/addin-max-connection-lifetime-kw
         :label         (loc/tr :security/plugin-max-lifetime-label)
         :tooltip-text  (loc/tr :security/plugin-max-lifetime-tooltip)}])]))

(defn session-timeout-settings
  "Session timeout settings - shows new keyword-based UI if migrated,
   otherwise falls back to legacy numeric hours input."
  [tenant-cursor]
  (let [has-new-timeout? (some? (:tenant/web-session-timeout-kw @tenant-cursor))]
    (if has-new-timeout?
      [new-session-timeout-settings tenant-cursor]
      [legacy-session-timeout-view tenant-cursor])))

(defn auth-method-view
  [tenant-cursor]
  (let [attribute :tenant/authentication-setting
        old-value (get @tenant-cursor attribute)
        options (->> authentication/auth-options
                     (map (fn [o]
                            {:value o
                             :label (loc/tr o)})))
        value (or old-value
                  :authentication.type/password-only)]
    [:div
     [labelled-input/view
      {:label     (loc/tr :company-settings-authentication)
       :data-type :data.type/keyword
       :value     (->> options
                       (cu/filter-by :value value)
                       (first))
       :options   options
       :on-change (fn [new-value]
                    (when-not (= new-value old-value)
                      (swap! tenant-cursor assoc attribute new-value)
                      (deref tenant-cursor)
                      (events/save-tenant!
                       (:db/id @tenant-cursor)
                       attribute
                       new-value
                       (fn [_]
                         (logging/info "Update successful"))
                       (fn [err]
                         (when (= new-value old-value)
                           (swap! tenant-cursor assoc attribute old-value)
                           (deref tenant-cursor))
                         (logging/error "Update failed " err)))))}]]))

(defn saml-attribute
  [tenant-cursor attribute error-messages]
  (let [current-value (get @tenant-cursor attribute)]
    [:div
     [labelled-input/view
      {:label     (let [fs (registry/get-field-schema @session/tenant-schema
                                                      attribute)]
                    (loc/field-name attribute fs))
       :data-type :data.type/text-simple
       :value     current-value
       :on-change (fn [current-value]
                    (let [error (cond (cu/blank-value? current-value)
                                      nil
                                      (not (url/valid-url? current-value))
                                      (loc/tr :not-valid-web-address)
                                      (not (string/starts-with? current-value "https:"))
                                      (loc/tr :only-https-url-accepted))]
                      (swap! tenant-cursor assoc attribute current-value)
                      (if error
                        (swap! error-messages assoc-in [:error-message attribute] error)
                        (swap! error-messages assoc-in [:error-message attribute] nil))))
       :on-blur   (fn [new-value]
                    (let [error? (get-in @error-messages [:error-message attribute])]
                      (when-not error?
                        (events/save-tenant!
                         (:db/id @tenant-cursor)
                         attribute
                         new-value
                         (fn [_]
                           (logging/info "Save successful"))
                         (fn [_]
                           (logging/error "Failed to save " (loc/tr attribute)))))))}]
     [:span {:class "text-xs text-danger-light-text"} (get-in @error-messages [:error-message attribute])]]))

(defn saml-idp-entity-id
  [tenant-cursor attribute]
  (let [{:keys [tenant/saml-idp-entity-id]} @tenant-cursor]
    [:div
     [labelled-input/view
      {:label     (let [fs (registry/get-field-schema @session/tenant-schema
                                                      attribute)]
                    (loc/field-name attribute fs))
       :data-type :data.type/text-simple
       :value     saml-idp-entity-id
       :on-change (fn [current-value]
                    (swap! tenant-cursor assoc attribute current-value))
       :on-blur   (fn [new-value]
                    (events/save-tenant!
                     (:db/id @tenant-cursor)
                     attribute
                     new-value
                     (fn [_]
                       (logging/info "Save successful"))
                     (fn [_]
                       (logging/error "Failed to save " (loc/tr attribute)))))}]]))

(defn saml-idp-public-key
  [tenant-cursor attribute]
  (let [saml-idp-public-key (->> @tenant-cursor
                                 :tenant/saml-idp-public-key
                                 (pem/format-pem-line-breaks))]
    [labelled-input/view
     {:label     (let [fs (registry/get-field-schema @session/tenant-schema
                                                     attribute)]
                   (loc/field-name attribute fs))
      :data-type :data.type/text-simple
      :value     saml-idp-public-key
      :on-change (fn [current-value]
                   (swap! tenant-cursor assoc attribute current-value))
      :on-blur   (fn [new-value]
                   (events/save-tenant!
                    (:db/id @tenant-cursor)
                    attribute
                    new-value
                    (fn [_]
                      (logging/info "Save successful"))
                    (fn [_]
                      (logging/error "Failed to save " (loc/tr attribute)))))}]))

(defn saml-sso-config
  [tenant-cursor]
  (let [metadata-url (str @app-db/server-url "/login/sso/saml/sp")
        error-messages (r/atom {})]
    [:div
     {:class "flex flex-col gap-4"}
     [:div
      {:class "font-bold text-xl"}
      (loc/tr :security/sso-settings)]
     [:div
      {:class "flex flex-col gap-2"}
      [:p
       (loc/tr :saml-config-info)
       [:a
        {:target "zapflow-saml-sp"
         :href   metadata-url}
        metadata-url]]
      [:p (loc/tr :saml-config-info-2)]]
     [saml-attribute tenant-cursor :tenant/saml-idp-post-url error-messages]
     [saml-attribute tenant-cursor :tenant/saml-idp-redirect-url error-messages]
     [saml-attribute tenant-cursor :tenant/saml-idp-logout-url error-messages]
     [saml-idp-entity-id tenant-cursor :tenant/saml-idp-entity-id]
     [saml-idp-public-key tenant-cursor :tenant/saml-idp-public-key]]))

(defn azure-attr
  [{:keys [tenant-cursor attribute placeholder]}]
  (let [current-value (get @tenant-cursor attribute)]
    [:div [labelled-input/view
           {:label     (let [fs (registry/get-field-schema @session/tenant-schema
                                                           attribute)]
                         (loc/field-name attribute fs))
            :data-type :data.type/text-simple
            :value     current-value
            :on-change (fn [current-value]
                         (swap! tenant-cursor assoc attribute current-value))
            :on-blur   (fn [new-value]
                         (events/save-tenant!
                          (:db/id @tenant-cursor)
                          attribute
                          new-value
                          (fn [_]
                            (logging/info "Save successful"))
                          (fn [_]
                            (logging/error "Failed to save " (loc/tr attribute)))))}]
     [:span {:class "text-xs"} placeholder]]))

(defn azure-sso-config
  [tenant-cursor]
  (let [redirect-url (str @app-db/server-url "/login/sso/azure/auth-response")
        logout-url (str @app-db/server-url "/login/sso/azure/logout-response")]
    [:div
     {:class "flex flex-col gap-4"}
     [:span
      {:class "text-xl font-bold"}
      "Azure configurations"]
     [:div
      {:class "flex flex-col gap-2"}
      [:div
       {:class "text-base-light-text text-xm"}
       (loc/tr :azure-config-info)]
      [:div
       {:class "text-base-light-text text-xm"}
       (loc/tr :azure-config-info-2)]]

     [:div
      {:class "flex flex-col gap-4"}
      [:div
       {:class "p-3 flex flex-col gap-2 bg-base-regular rounded"}
       [:label
        {:class "text-base-light-text-paler text-xm"}
        (loc/tr :azure-redirect-url)]
       [:div
        redirect-url]]
      [:div
       {:class "p-3 flex flex-col gap-2 bg-base-regular rounded"}
       [:label
        (loc/tr :azure-logout-url)]
       [:div
        logout-url]]]
     [azure-attr
      {:tenant-cursor tenant-cursor
       :placeholder   "Directory (tenant) ID"               ;; not translated as these are from Azure
       :attribute     :tenant/azure-sso-tenant-id}]
     [azure-attr
      {:tenant-cursor tenant-cursor
       :placeholder   "Application (client) ID"             ;; not translated as these are from Azure
       :attribute     :tenant/azure-sso-client-id}]
     [azure-attr
      {:tenant-cursor tenant-cursor
       :placeholder   (loc/tr :azure-secret-value-placeholder)
       :attribute     :tenant/azure-sso-client-secret}]
     [azure-attr
      {:tenant-cursor tenant-cursor
       :placeholder   "https://login.microsoftonline.com/<Directory id>"
       :attribute     :tenant/azure-sso-authority}]
     [azure-attr
      {:tenant-cursor tenant-cursor
       :placeholder   "email"                               ;; not translated as this is Azure app enum
       :attribute     :tenant/azure-sso-scope}]]))

(defn sso-login
  [tenant-cursor]
  (let [attribute :tenant/block-password-login
        old-value (get @tenant-cursor attribute)
        options [{:value :yes
                  :label (loc/tr :yes)}
                 {:value :no
                  :label (loc/tr :no)}]]
    [:div
     [labelled-input/view
      {:label     (loc/tr :company-settings-block-password-login)
       :data-type :data.type/keyword
       :value     (->> options
                       (cu/filter-by :value (if (:tenant/block-password-login @tenant-cursor)
                                              :yes
                                              :no))
                       (first))
       :options   options
       :on-change (fn [new-value]
                    (let [new-value (case new-value
                                      :yes true
                                      :no false)]
                      (when-not (= new-value old-value)
                        (swap! tenant-cursor assoc attribute new-value)
                        (deref tenant-cursor)
                        (events/save-tenant!
                         (:db/id @tenant-cursor)
                         attribute
                         new-value
                         (fn [_]
                           (logging/info "Update successful"))
                         (fn [err]
                           (when (= new-value old-value)
                             (swap! tenant-cursor assoc attribute old-value)
                             (deref tenant-cursor))
                           (logging/error "Update failed " err))))))}]]))

(defn security-settings
  [tenant-cursor]
  (let [feature-2fa? (plan/available :feature/authentication-2fa @tenant-cursor :role/admin)]
    [:div
     {:class "flex flex-col gap-6"}
     [session-timeout-settings tenant-cursor]
     (when (and feature-2fa?
                (not (:tenant/block-password-login @tenant-cursor)))
       [:div
        {:class "flex flex-col gap-4"}
        [:div
         {:class "font-bold text-xl"}
         (loc/tr :security/settings)]
        [auth-method-view tenant-cursor]])]))

(defn legacy-sso-settings
  [tenant-cursor]
  (let [sso-methods (not-empty (:tenant/sso-logins @tenant-cursor))]
    [:div
     [:div
      [:div
       {:class "flex flex-col gap-6"}
       (when (plan/available :feature/saml-sso @tenant-cursor :role/admin)
         [saml-sso-config tenant-cursor])
       (when (plan/available :feature/azure-sso @tenant-cursor :role/admin)
         [azure-sso-config tenant-cursor])
       (when sso-methods
         [sso-login tenant-cursor])]]]))

(defn login-activity
  []
  (let [_ (events/get-tokens)]
    (fn []
      (let [tokens (:tokens @db/state)
            show-all? false
            selected-tokens (if show-all?
                              tokens
                              (take 25 tokens))]
        [:div
         {:class "flex flex-col gap-3"}
         [:div
          {:class "font-bold text-xl"}
          (loc/tr :security/login-activity)]
         [:div
          {:class "flex flex-col gap-4"}
          [:div
           (loc/tr :settings/login-activity-info)
           [:a
            {:href       "#"
             :class-name "inline-block"
             :on-click   (fn [e]
                           (dg/prevent-and-stop e)
                           (events/get-logins))}
            (loc/tr :here)]
           "."]
          [:div
           {:class "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
           [:table
            {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
            [:thead
             [:tr
              {:class "*:px-3 *:py-3.5 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
              ;  [:th (loc/tr :account/first-name)]
              ;  [:th (loc/tr :account/last-name)]
              [:th (loc/tr :account/username)]
              [:th (loc/tr :token/updated)]
              [:th (loc/tr :settings/duration-minutes)]
              ;  [:th (loc/tr :token/device-type)]
              [:th (loc/tr :token/device-id)]]]
            [:tbody
             {:class "divide-y divide-solid divide-base-light-border-paler"}
             (doall
              (for [token selected-tokens
                    :let [{:keys [token/subject token/created token/expires]} token
                          duration (-> (- expires created)
                                       (/ 1000)
                                       (quot 60))
                          account (->> subject
                                       (get @session/entities))
                          {:keys [account/email]} account]]
                [:tr
                 {:key   (str (:db/id token))
                  :class "*:px-3 *:py-4 *:whitespace-nowrap *:text-sm *:text-base-light-text *:align-middle"}
                  ; [:td first-name]
                  ; [:td last-name]
                 [:td email]
                 [:td (zap-time/long->formatted-date-time (:token/created token))]
                 [:td (str " < " duration)]
                  ; [:td (:token/device-type token)]
                 [:td (:token/device-id token)]]))]]]]]))))

(defn export-activity
  []
  (let [exports (->> @db/exports
                     vals
                     (cu/only-entity :attachment/type :attachment.type/export)
                     (sort-by :attachment/created)
                     reverse
                     (take 25))]
    [:div
     [:div
      [:div
       {:class "max-w-full flex flex-col gap-3"}
       [:div
        {:class "font-bold text-xl"}
        (loc/tr :security/export-activity)]
       [:div
        {:class "flex flex-col gap-4"}
        [:div
         {:class "text-base-light-text-paler"}
         (loc/tr :settings/export-activity-info)
         [:a
          {:href       "#"
           :class-name "inline-block"
           :on-click   (fn [e]
                         (dg/prevent-and-stop e)
                         (events/get-exports))}
          (loc/tr :here)]
         "."]
        [:div
         {:class "max-w-full overflow-auto border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
         [:div
          {:class "w-full inline-block"}
          [:table
           {:class "w-full min-w-full divide-y divide-solid divide-base-light-border-paler"}
           [:thead
            [:tr
             {:class "*:px-3 *:py-3.5 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
             [:th (loc/tr :export-timestamp)]
             [:th (loc/tr :export-download)]
             [:th (loc/tr :export-created-by)]]]
           [:tbody
            {:class "divide-y divide-solid divide-base-light-border-paler"}
            (doall
             (for [export exports]
               (let [uploader (:attachment/uploader export)
                     filename (:attachment/filename export)
                     account (get @session/entities uploader)]
                 [:tr
                  {:key   (:db/id export)
                   :id    (str "export-" (:db/id export))
                   :class "*:px-3 *:py-4 *:whitespace-nowrap *:text-sm *:text-base-light-text *:align-middle"}
                  [:td (zap-time/long->formatted-date-time (:attachment/created export))]
                  [:td
                   [:div
                    {:class    "flex items-center gap-2 cursor-pointer text-base-light-text hover:text-base-light-text-accent"
                     :on-click #(fu/open-attachment! (:db/id export))}
                    [:i.fal.fa-file-excel]
                    [:span
                     {:class "text-ellipsis overflow-hidden"}
                     filename]]]
                  [:td
                   (str (:account/first-name account) " " (:account/last-name account))]])))]]]]]]]]))

(defn security-view
  [tenant-cursor]
  [:div
   {:class "flex flex-col gap-8"}
   [security-settings tenant-cursor]
   (when (or (plan/available :feature/saml-sso @tenant-cursor)
             (plan/available :feature/azure-sso @tenant-cursor)
             (plan/available :feature/new-sso-2025 @tenant-cursor))
     (if (plan/available :feature/new-sso-2025 @tenant-cursor)
       [new-sso-2025/sso-view]
       [legacy-sso-settings tenant-cursor]))
   [login-activity]
   [export-activity tenant-cursor]])

(defn component
  []
  (let [tenant-id (:db/id @session/tenant)
        tenant-cursor (r/cursor session/entities [tenant-id])]
    (fn []
      [:<>
       [zap-toolbar/view db/state breadcrumbs nil nil nil]
       [:div
        {:class "bg-base-light text-base-light-text h-[calc(100vh_-_52px-_37px)] overflow-auto"}
        [:div
         {:class "px-4 py-6 mx-auto xl:w-5xl"}
         [security-view tenant-cursor]]]])))

(defmethod routing/render-view :new-settings/security
  [_]
  (if (session/is-admin?)
    [default-page-layout/view
     {:class-name ""}
     [component]]
    [not-found/not-found-view]))
